import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Menu, X } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import CartDrawer from "./CartDrawer";

export default function Header() {
  const nav = useNavigate();
  const loc = useLocation();
  const { count, setOpen } = useCart();
  const { items: wish } = useWishlist();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [ac, setAc] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [tab, setTab] = useState(() => (loc.pathname.includes("/kids") ? "kids" : "women"));

  useEffect(() => {
    if (!q) { setAc([]); return; }
    const t = setTimeout(() => {
      api.get(`/products/autocomplete?q=${encodeURIComponent(q)}`).then((r) => setAc(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const goSearch = () => { if (q) { nav(`/shop?q=${encodeURIComponent(q)}`); setAc([]); setShowSearch(false); } };

  return (
    <>
      <div className="bg-[#0B1F3A] text-[#FFF9E6] text-[10px] md:text-xs py-1.5 md:py-2 text-center px-4" data-testid="announcement-bar">
        <span className="tracking-widest">FREE INDIA SHIPPING · WHATSAPP ORDERING</span>
      </div>
      <header className="sticky top-0 z-40 bg-[#FCE4EC]/95 backdrop-blur-md border-b" style={{ borderColor: "rgba(114,47,55,0.2)" }}>
        <div className="max-w-7xl mx-auto px-3 md:px-8 py-2 md:py-4 flex items-center gap-1.5 md:gap-6">
          <button className="md:hidden p-1" onClick={() => setShowMenu(true)} data-testid="mobile-menu-btn"><Menu size={22} /></button>
          <Link to="/" className="shrink-0 mr-auto md:mr-0" data-testid="logo-link">
            <div className="brand-mark">
              <span className="brand-nisa text-3xl md:text-5xl">Nisa</span>
              <span className="brand-underline" />
              <span className="brand-libas text-[9px] md:text-xs">LIBAS</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            <button onClick={() => { setTab("women"); nav("/shop/women"); }}
              className={`px-4 py-2 text-sm tracking-wider uppercase font-medium transition-all ${tab === "women" ? "text-[#0B1F3A] border-b-2 border-[#D4AF37]" : "text-[#1E293B]/60 hover:text-[#0B1F3A]"}`}
              data-testid="nav-women">Women</button>
            <button onClick={() => { setTab("kids"); nav("/shop/kids"); }}
              className={`px-4 py-2 text-sm tracking-wider uppercase font-medium transition-all ${tab === "kids" ? "text-[#0B1F3A] border-b-2 border-[#D4AF37]" : "text-[#1E293B]/60 hover:text-[#0B1F3A]"}`}
              data-testid="nav-kids">Kids</button>
            <Link to="/track" className="px-4 py-2 text-sm tracking-wider uppercase font-medium text-[#1E293B]/60 hover:text-[#0B1F3A]" data-testid="nav-track">Track Order</Link>
          </nav>

          <div className="hidden md:flex flex-1 max-w-md ml-auto relative">
            <div className="flex items-center gap-2 w-full bg-white border rounded-sm px-3 py-2" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
              <Search size={18} className="text-[#0B1F3A]/60" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goSearch()}
                placeholder="Search sarees, lehengas, kids…"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#1E293B]/40"
                data-testid="search-input" />
            </div>
            {ac.length > 0 && (
              <div className="absolute top-12 left-0 right-0 bg-white shadow-xl border rounded-sm z-50" style={{ borderColor: "rgba(212,175,55,0.35)" }} data-testid="autocomplete">
                {ac.map((p) => (
                  <Link key={p.id} to={`/product/${p.slug}`} onClick={() => { setQ(""); setAc([]); }}
                    className="flex items-center gap-3 p-3 hover:bg-[#FCE4EC] border-b last:border-0"
                    style={{ borderColor: "rgba(212,175,55,0.15)" }} data-testid={`ac-item-${p.id}`}>
                    <img src={p.images?.[0]} className="w-10 h-14 object-cover" alt="" />
                    <div className="flex-1 text-sm text-[#0B1F3A]">{p.name}</div>
                    <div className="text-sm font-semibold text-[#D4AF37]">{inr(p.price)}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => setShowSearch(true)} className="md:hidden p-2 -mr-1" data-testid="mobile-search-btn"><Search size={22} /></button>
          <Link to="/wishlist" className="relative p-2 hover:text-[#D4AF37]" data-testid="wishlist-link">
            <Heart size={22} className="md:w-5 md:h-5" />
            {wish.length > 0 && <span className="absolute top-0 right-0 bg-[#D4AF37] text-[10px] text-[#0B1F3A] w-4 h-4 rounded-full flex items-center justify-center font-semibold">{wish.length}</span>}
          </Link>
          <button onClick={() => setOpen(true)} className="relative p-2 hover:text-[#D4AF37]" data-testid="cart-btn">
            <ShoppingBag size={22} className="md:w-5 md:h-5" />
            {count > 0 && <span className="absolute top-0 right-0 bg-[#D4AF37] text-[10px] text-[#0B1F3A] w-4 h-4 rounded-full flex items-center justify-center font-semibold" data-testid="cart-count">{count}</span>}
          </button>
          {user ? (
            <div className="relative group hidden md:block">
              <button className="p-2 hover:text-[#D4AF37]" data-testid="user-menu-btn"><User size={20} /></button>
              <div className="absolute right-0 top-10 w-44 bg-white shadow-xl border p-2 hidden group-hover:block z-50" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
                <div className="px-3 py-2 text-xs text-[#1E293B]/60 border-b" style={{ borderColor: "rgba(212,175,55,0.25)" }}>{user.email}</div>
                {user.role === "admin" && <Link to="/admin" className="block px-3 py-2 text-sm hover:bg-[#FCE4EC]" data-testid="admin-link">Admin</Link>}
                <Link to="/orders" className="block px-3 py-2 text-sm hover:bg-[#FCE4EC]" data-testid="orders-link">My Orders</Link>
                <button onClick={logout} className="block w-full text-left px-3 py-2 text-sm hover:bg-[#FCE4EC]" data-testid="logout-btn">Logout</button>
              </div>
            </div>
          ) : (
            <Link to="/login" className="hidden md:block text-sm hover:text-[#D4AF37]" data-testid="login-link">Login</Link>
          )}
        </div>
      </header>

      {/* Mobile menu */}
      {showMenu && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setShowMenu(false)}>
          <div className="w-72 h-full bg-[#FCE4EC] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div className="brand-mark"><span className="brand-nisa text-3xl">Nisa</span><span className="brand-underline" /><span className="brand-libas text-[9px]">LIBAS</span></div>
              <button onClick={() => setShowMenu(false)} data-testid="close-menu"><X size={22} /></button>
            </div>
            {[["Women","/shop/women"],["Kids","/shop/kids"],["Wishlist","/wishlist"],["Track Order","/track"],["Login","/login"]].map(([l,h]) => (
              <Link key={h} to={h} onClick={() => setShowMenu(false)} className="block py-3 border-b text-[#0B1F3A]" style={{ borderColor: "rgba(212,175,55,0.25)" }} data-testid={`m-${l.toLowerCase().replace(' ','-')}`}>{l}</Link>
            ))}
          </div>
        </div>
      )}
      {showSearch && (
        <div className="fixed inset-0 z-50 bg-[#FCE4EC] p-4">
          <div className="flex items-center gap-2 mb-3">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goSearch()}
              placeholder="Search…" className="flex-1 bg-white border px-3 py-3 outline-none rounded-sm" style={{ borderColor: "rgba(212,175,55,0.35)" }} data-testid="mobile-search-input" />
            <button onClick={() => setShowSearch(false)} data-testid="close-search"><X size={22} /></button>
          </div>
          {ac.map((p) => (
            <Link key={p.id} to={`/product/${p.slug}`} onClick={() => setShowSearch(false)} className="flex items-center gap-3 p-3 border-b" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
              <img src={p.images?.[0]} className="w-12 h-16 object-cover" alt="" />
              <div className="flex-1 text-sm">{p.name}</div>
              <div className="text-sm font-semibold text-[#D4AF37]">{inr(p.price)}</div>
            </Link>
          ))}
        </div>
      )}
      <CartDrawer />
    </>
  );
}
